import { motion, AnimatePresence } from "framer-motion";
import { X, Info } from "lucide-react";

/**
 * Premium single-action info modal.
 * Used for notices that only need an acknowledgement (OK button),
 * e.g. "Your profile needs to be verified by admin before you can send interests."
 *
 * tone: 'brand' | 'warning' | 'success' | 'danger'
 */
const tones = {
  brand:   { iconBg: "bg-brand-50",   iconText: "text-brand-600",   button: "btn-primary" },
  warning: { iconBg: "bg-amber-50",   iconText: "text-amber-600",   button: "inline-flex items-center justify-center gap-2 rounded-2xl bg-amber-500 px-5 py-3 text-[14px] font-semibold text-white shadow-soft transition hover:bg-amber-600 active:scale-[0.98]" },
  success: { iconBg: "bg-emerald-50", iconText: "text-emerald-600", button: "inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-[14px] font-semibold text-white shadow-soft transition hover:bg-emerald-700 active:scale-[0.98]" },
  danger:  { iconBg: "bg-rose-50",    iconText: "text-rose-600",    button: "inline-flex items-center justify-center gap-2 rounded-2xl bg-rose-600 px-5 py-3 text-[14px] font-semibold text-white shadow-soft transition hover:bg-rose-700 active:scale-[0.98]" },
};

export default function InfoModal({
  open,
  title,
  description,
  Icon = Info,
  tone = "brand",
  okLabel = "OK",
  onClose,
  children,
}) {
  const t = tones[tone] || tones.brand;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => onClose?.()}
          className="fixed inset-0 z-[90] flex items-end justify-center bg-ink-900/50 px-4 backdrop-blur-sm sm:items-center"
        >
          <motion.div
            initial={{ y: 32, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 24, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md overflow-hidden rounded-t-[28px] bg-white shadow-elevated sm:rounded-3xl"
          >
            <div className="flex items-start gap-4 px-6 pt-6">
              <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ${t.iconBg} ${t.iconText}`}>
                <Icon size={24} />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-xl font-bold text-ink-800">{title}</h3>
                {description && (
                  <p className="mt-1.5 text-[13.5px] leading-6 text-ink-500">{description}</p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="btn-icon h-9 w-9"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            {children && <div className="px-6 pb-1 pt-4">{children}</div>}

            <div className="px-6 py-5">
              <button type="button" onClick={onClose} className={`${t.button} w-full`}>
                {okLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
